import { attributionCertainty, isGenericClusterLabel } from '../../utils/canvasAssurance';

export type Section91Basis =
    | { show: true; basis: string }
    | { show: false; reason: 'cluster_tag' | 'not_verified' | 'no_attribution' };

/**
 * Whether the Node Inspector banner may recommend a Section 91 CrPC / Section 94
 * BNSS notice to the exchange behind this node (WB09).
 *
 * An exchange entity type alone is not enough: the type is also set by heuristics and by
 * "Brand. User" cluster tags, which mean "counterparty of the brand's cluster", not "owned
 * by the brand". The notice is recommended only when the attribution carries a verified
 * source or a provider label, or an explicit confidence of at least 0.5 on an attribution
 * whose method is not recorded. A cluster tag, a heuristic or a bare entity type is never
 * enough. Pure.
 */
export function section91Basis(node: any): Section91Basis {
    if (!node) return { show: false, reason: 'no_attribution' };
    const name = typeof node.entity_name === 'string' ? node.entity_name : '';
    if (name && isGenericClusterLabel(name)) {
        const info = attributionCertainty(node);
        // An analyst or proof of reserves can override a cluster tag; everything else cannot.
        if (!info || info.certainty !== 'verified') return { show: false, reason: 'cluster_tag' };
    }
    const info = attributionCertainty(node);
    if (!info) return { show: false, reason: 'no_attribution' };
    if (info.certainty === 'verified') return { show: true, basis: info.text };
    if (info.certainty === 'provider') return { show: true, basis: info.text };
    const conf = [node.attribution_confidence, node.entity_confidence, node.confidence]
        .find((c) => typeof c === 'number' && Number.isFinite(c)) as number | undefined;
    if (info.certainty === 'unspecified' && typeof conf === 'number' && conf >= 0.5) {
        return { show: true, basis: `${info.text} (confidence ${Math.round(conf * 100)}%)` };
    }
    return { show: false, reason: info.certainty === 'cluster' ? 'cluster_tag' : 'not_verified' };
}
